import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API_BASE_URL } from '@gisgo/shared';

import { LocalizedText } from './localized-text';

/** Contrats administration livreurs (feature C). */

export type DriverAccountStatus = 'kyc_pending' | 'active' | 'suspended';

export interface DriverRow {
  readonly driver_user_id: number;
  readonly name: string;
  readonly phone: string | null;
  readonly status: DriverAccountStatus;
  readonly is_online: boolean;
  readonly vehicle_type: string | null;
  readonly wallet_balance: number;
  readonly cash_due: string;
  readonly completed_orders_count: number;
  readonly created_at: string | null;
}

export interface KycDocument {
  readonly id: number;
  readonly type: string;
  readonly status: 'pending' | 'approved' | 'rejected' | 'superseded';
  readonly rejection_reason: string | null;
  readonly file_url: string;
  readonly uploaded_at: string;
}

export interface DriverAccount {
  readonly driver_user_id: number;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
  readonly status: DriverAccountStatus;
  readonly is_online: boolean;
  readonly vehicle_type: string | null;
  readonly vehicle_plate: string | null;
  readonly kyc_validated_at: string | null;
  readonly kyc: {
    readonly required: readonly string[];
    readonly is_complete: boolean;
    readonly documents: readonly KycDocument[];
  };
  readonly cash: { readonly collected: string; readonly settled: string; readonly due: string };
}

/** Position cash d'un livreur (circuit cash v2 : avance resto au retrait). */
export interface DriverCash {
  /** Σ cash encaissé chez les clients (courses espèces livrées). */
  readonly collected: string;
  /** Σ part resto avancée en espèces au retrait. */
  readonly advanced: string;
  /** Σ gains livreur conservés dans le cash encaissé. */
  readonly earned_cash: string;
  /** Σ reversements encaissés au hub (reçus). */
  readonly settled: string;
  /** Position exacte due à la plateforme (signée). */
  readonly due: string;
}

/** Types d'écriture du ledger (miroir de LedgerEntryType, module Wallet). */
export type LedgerEntryType =
  | 'order_payment'
  | 'order_payout_restaurant'
  | 'order_payout_driver'
  | 'cash_deposit'
  | 'cash_auto_settlement'
  | 'payout_request'
  | 'payout_processed'
  | 'refund'
  | 'promo_redemption'
  | 'adjustment'
  | 'manual_transfer'
  | 'marketing_credit';

export interface DriverWalletTransaction {
  readonly id: number;
  readonly direction: 'credit' | 'debit';
  readonly amount: number;
  readonly balance_after: number;
  readonly created_at: string;
  readonly type: LedgerEntryType | null;
  readonly description: string | null;
  /** Staff à l'origine de l'écriture (ajustement, payout…), sinon `null` (automatique). */
  readonly created_by: string | null;
  /** Commande à l'origine de l'écriture (paiement / règlement cash), sinon `null`. */
  readonly order: {
    readonly id: number;
    readonly public_code: string;
    readonly payment_method: string | null;
  } | null;
}

export interface DriverWalletSettlement {
  readonly id: number;
  readonly amount: string;
  readonly receipt_no: string;
  readonly settled_by: string | null;
  readonly created_at: string;
}

export interface DriverWalletPayout {
  readonly id: number;
  readonly amount: number;
  readonly method: 'bank_transfer' | 'cash_pickup' | 'other';
  readonly status: 'pending' | 'processing' | 'paid' | 'failed';
  readonly requested_at: string | null;
  readonly processed_at: string | null;
  readonly reason: string | null;
}

export interface DriverWallet {
  readonly balance: number;
  readonly currency: string;
  readonly cash: DriverCash;
  readonly transactions: readonly DriverWalletTransaction[];
  readonly settlements: readonly DriverWalletSettlement[];
  readonly payouts: readonly DriverWalletPayout[];
}

/** Ligne de l'historique des courses (GET /ops/drivers/{id}/deliveries). */
export interface DriverDelivery {
  readonly id: number;
  readonly public_code: string;
  readonly status: 'delivered' | 'failed' | 'cancelled' | string;
  readonly originator_type: string;
  readonly accepted_at: string | null;
  readonly pickup_at: string | null;
  readonly delivered_at: string | null;
  readonly cancelled_at: string | null;
  readonly failure_reason: string | null;
  readonly cancellation_reason: string | null;
  readonly distance_km: string | null;
  readonly estimated_minutes: number | null;
  readonly delivery_fee: string;
  readonly cash_to_collect: string | null;
  readonly pickup: { readonly name: string | null; readonly address: string | null };
  readonly dropoff: { readonly name: string | null; readonly address: string | null };
  readonly order: {
    readonly id: number;
    readonly public_code: string;
    readonly status: string;
    readonly payment_method: string | null;
    readonly items_count: number;
    readonly subtotal: string;
    readonly delivery_fee: string;
    readonly discount_amount: string;
    readonly commission_amount: string;
    readonly restaurant_payout: string;
    readonly total: string;
    readonly store: { readonly id: number; readonly name: LocalizedText } | null;
    readonly client: {
      readonly id: number;
      readonly name: string;
      readonly phone: string | null;
    } | null;
  } | null;
  /** Ventilation du cash passé par les mains du livreur — courses espèces livrées uniquement. */
  readonly cash: {
    readonly collected: string;
    readonly advanced: string;
    readonly earned: string;
    readonly platform_due: string;
  } | null;
}

/** Totaux sur l'ensemble des courses livrées du livreur (indépendants des filtres). */
export interface DriverDeliveriesSummary {
  readonly delivered_count: number;
  readonly failed_count: number;
  readonly cancelled_count: number;
  readonly cash_orders_count: number;
  readonly delivery_fees: string;
  readonly cash_collected: string;
  readonly cash_advanced: string;
  readonly cash_earned: string;
  readonly cash_platform_due: string;
}

export interface DriverDeliveriesPage {
  readonly data: readonly DriverDelivery[];
  readonly meta: {
    readonly current_page: number;
    readonly last_page: number;
    readonly per_page: number;
    readonly total: number;
    readonly summary: DriverDeliveriesSummary;
  };
}

@Injectable({ providedIn: 'root' })
export class DriversApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  drivers(): Observable<DriverRow[]> {
    return this.http
      .get<{ data: DriverRow[] }>(`${this.baseUrl}/ops/drivers`)
      .pipe(map((response) => response.data));
  }

  account(driverUserId: number): Observable<DriverAccount> {
    return this.http
      .get<{ data: DriverAccount }>(`${this.baseUrl}/ops/drivers/${driverUserId}/account`)
      .pipe(map((response) => response.data));
  }

  reviewDocument(
    driverUserId: number,
    documentId: number,
    decision: 'approve' | 'reject',
    reason?: string,
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/ops/drivers/${driverUserId}/kyc/documents/${documentId}/${decision}`,
      reason !== undefined ? { reason } : {},
    );
  }

  approveAccount(driverUserId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/drivers/${driverUserId}/approve`, {});
  }

  suspend(driverUserId: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/drivers/${driverUserId}/suspend`, { reason });
  }

  reinstate(driverUserId: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/drivers/${driverUserId}/reinstate`, {
      reason,
    });
  }

  /**
   * Historique des courses — params déjà traduits (`status`, `payment`,
   * `from`, `until`, `page` 1-based), transmis tels quels.
   */
  deliveries(
    driverUserId: number,
    params: Record<string, string>,
  ): Observable<DriverDeliveriesPage> {
    return this.http.get<DriverDeliveriesPage>(
      `${this.baseUrl}/ops/drivers/${driverUserId}/deliveries`,
      { params: new HttpParams({ fromObject: params }) },
    );
  }

  wallet(driverUserId: number): Observable<DriverWallet> {
    return this.http
      .get<{ data: DriverWallet }>(`${this.baseUrl}/ops/drivers/${driverUserId}/wallet`)
      .pipe(map((response) => response.data));
  }

  settleCash(driverUserId: number, amount: string, receiptNo: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/drivers/${driverUserId}/wallet/settle-cash`, {
      amount,
      receipt_no: receiptNo,
    });
  }

  adjust(driverUserId: number, amount: string, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/drivers/${driverUserId}/wallet/adjust`, {
      amount,
      reason,
    });
  }

  payout(driverUserId: number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/ops/drivers/${driverUserId}/wallet/payout`, {
      reason,
    });
  }
}
